/** Static copy and metadata for workflow stepper nodes (matches mockup). */

import { V7_ANALYSIS_PIPELINE_STEPS, phaseLabelExtended } from './v7AnalysisPipeline';
import type { WorkflowStepKey } from '../types/workflow';

export type WorkflowStepMeta = {
  key: WorkflowStepKey;
  number: string;
  title: string;
  description: string;
  comingSoon?: boolean;
};

export const WORKFLOW_STEP_META: readonly WorkflowStepMeta[] = [
  {
    key: 'upload',
    number: '01',
    title: 'Upload Script',
    description: 'Bring your draft into PayorPlay.',
  },
  {
    key: 'analyse',
    number: '02',
    title: 'Analyse Script',
    description: 'Run the diagnostic pass.',
  },
  {
    key: 'generate_scorecard',
    number: '03',
    title: 'Generate Scorecard',
    description: 'Compute commercial validation metrics.',
  },
  {
    key: 'rebuild',
    number: '04',
    title: 'Rebuild Script',
    description: 'Apply recommended revisions.',
  },
];

function stepAgentId(step: (typeof V7_ANALYSIS_PIPELINE_STEPS)[number]): string | null {
  // Methodology steps expose agentId; keep a soft fallback for older shapes.
  const anyStep = step as { agentId?: string | null; agentName?: string | null };
  return anyStep.agentId ?? anyStep.agentName ?? null;
}

/** Map SSE agent / methodology agent_id to human-readable pipeline label. */
export function agentLabel(agentName: string | null | undefined): string | null {
  if (!agentName) return null;
  const step = V7_ANALYSIS_PIPELINE_STEPS.find((s) => stepAgentId(s) === agentName || s.id === agentName);
  return step?.label ?? agentName;
}

/** Map pipeline phase id to human-readable label. */
export function phaseLabel(phaseId: string | null | undefined): string | null {
  return phaseLabelExtended(phaseId);
}

function agentPrefixFromProgressId(phaseId: string): string {
  // Strip named-strand prefix (will_ambition_distance:1.7.3 or strand:A:1.7.3)
  const bare = phaseId.includes(':')
    ? phaseId.split(':').slice(-1)[0]
    : phaseId;
  const m = /^(\d+\.\d+[a-z]?)/.exec(bare);
  return m ? m[1] : bare;
}

/** First pipeline phase not yet present in completed_phases (likely running or next). */
export function inferActivePhaseId(completedPhases: string[]): string | null {
  const completed = new Set(completedPhases);
  // completed may be step_ids (1.7.3) or strand:step — treat agent prefix as done
  const completedAgents = new Set(completedPhases.map(agentPrefixFromProgressId));
  for (const step of V7_ANALYSIS_PIPELINE_STEPS) {
    if (step.id === 'upload') continue;
    if (!completed.has(step.id) && !completedAgents.has(step.id)) {
      return step.id;
    }
  }
  return null;
}

/** First pipeline phase whose agent has not completed (fallback when phases absent). */
export function inferActivePhaseIdFromAgents(completedAgents: string[]): string | null {
  const completed = new Set(completedAgents);
  for (const step of V7_ANALYSIS_PIPELINE_STEPS) {
    const aid = stepAgentId(step);
    if (!aid) continue;
    if (!completed.has(aid) && !completed.has(step.id)) {
      return step.id;
    }
  }
  return null;
}

/** Resolve the agent likely running from poll/SSE-synced progress (mirrors buildAnalyseSubtext). */
export function inferCurrentAgentFromProgress(params: {
  currentAgent?: string | null;
  completedPhases?: string[];
  completedAgents?: string[];
}): string | null {
  if (params.currentAgent) return params.currentAgent;
  const phaseId =
    (params.completedPhases?.length ? inferActivePhaseId(params.completedPhases) : null) ??
    (params.completedAgents?.length ? inferActivePhaseIdFromAgents(params.completedAgents) : null);
  if (!phaseId) return null;
  const step = V7_ANALYSIS_PIPELINE_STEPS.find((s) => s.id === phaseId);
  return stepAgentId(step as (typeof V7_ANALYSIS_PIPELINE_STEPS)[number]) ?? phaseId;
}

/** Subtext for workflow analyse node — step progress only (no dominance/strand lists). */
export function buildAnalyseSubtext(params: {
  analyzing: boolean;
  currentAgent: string | null;
  completedPhases?: string[];
  completedAgents?: string[];
  multiStrandStatus?: string | null;
  strandId?: string | null;
  strandPhase?: string | null;
  currentStepId?: string | null;
}): string | null {
  if (!params.analyzing) return null;

  // Prefer the active step id; strip strand prefixes so named strands stay off the workflow UI.
  const stepHint =
    (params.currentStepId ? agentPrefixFromProgressId(params.currentStepId) : null) ||
    (params.strandPhase ? agentPrefixFromProgressId(params.strandPhase) : null) ||
    inferCurrentAgentFromProgress(params);

  const label =
    (stepHint ? phaseLabelExtended(stepHint) : null) ??
    agentLabel(stepHint) ??
    (stepHint ? stepHint : null);
  if (label) {
    return `Currently running: ${label}`;
  }
  return 'Currently running: initialising pipeline…';
}
